import { useState, useEffect } from 'react'
import styles from './GroupDetails.module.css'
import { AddUserToGroupModal, EditGroupModal, GroupReminderModal } from '../modals'
import apiClient from '../../utils/api'

/**
 * GroupDetails - Component for displaying detailed group information
 * 
 * Props:
 * - group: The group object to display details for
 * - onClose: Callback function when closing the details panel
 * - onEdit: Callback function when editing the group (optional)
 * - onManageMembers: Callback function when managing members (optional)
 * - onViewProjects: Callback function when viewing projects (optional)
 * - onArchive: Callback function when archiving the group (optional)
 */
export default function GroupDetails({
    group: initialGroup,
    onClose,
    onArchive,
    onGroupUpdate,
    apiToken,
    onSuccessMessage,
    onError
}) {
    const [group, setGroup] = useState(initialGroup)
    const [showAddMemberModal, setShowAddMemberModal] = useState(false)
    const [showEditGroupModal, setShowEditGroupModal] = useState(false)
    const [showReminderModal, setShowReminderModal] = useState(false)
    const [editingReminder, setEditingReminder] = useState(null)
    const [reminders, setReminders] = useState([])
    const [loadingReminders, setLoadingReminders] = useState(false)

    // Update local state when prop changes
    useEffect(() => {
        setGroup(initialGroup)
    }, [initialGroup])

    // Fetch reminders when group changes
    useEffect(() => {
        if (group?.id && apiToken) {
            fetchReminders()
        }
    }, [group?.id, apiToken])

    const fetchReminders = async () => {
        try {
            setLoadingReminders(true)
            apiClient.setAuthToken(apiToken)
            const response = await apiClient.getGroupReminders(group.id)
            setReminders(response.reminders || [])
        } catch (error) {
            console.error('Failed to fetch reminders:', error)
            if (onError) {
                onError('Failed to load reminders')
            }
        } finally {
            setLoadingReminders(false)
        }
    }

    if (!group) return null

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        })
    }

    const handleMemberAddedToGroup = (updatedMember) => {
        setShowAddMemberModal(false)
        if (onSuccessMessage) {
            onSuccessMessage('Member added to group successfully')
        }
        // Refresh the group data to show the new member
        if (onGroupUpdate && group) {
            onGroupUpdate(group.id)
        }
    }

    const handleRemoveMemberFromGroup = async (groupId, memberId) => {
        try {
            if (!apiToken) {
                throw new Error('Authentication token is required')
            }

            apiClient.setAuthToken(apiToken)
            await apiClient.removeMemberFromGroup(groupId, memberId)
            
            if (onSuccessMessage) {
                onSuccessMessage('Member removed from group successfully')
            }
            
            // Refresh the group data to reflect the removal
            if (onGroupUpdate) {
                onGroupUpdate(groupId)
            }
        } catch (error) {
            console.error('Failed to remove member from group:', error)
            if (onError) {
                onError('Failed to remove member from group')
            }
        }
    }

    const handleGroupUpdated = (updatedGroup) => {
        setShowEditGroupModal(false)
        if (onSuccessMessage) {
            onSuccessMessage('Group updated successfully')
        }
        // Refresh the group data to show the updated information
        if (onGroupUpdate && group) {
            onGroupUpdate(group.id)
        }
    }

    const handleAddReminder = () => {
        setEditingReminder(null)
        setShowReminderModal(true)
    }

    const handleEditReminder = (reminder) => {
        setEditingReminder(reminder)
        setShowReminderModal(true)
    }

    const handleReminderSaved = (savedReminder) => {
        setShowReminderModal(false)
        setEditingReminder(null)
        if (onSuccessMessage) {
            onSuccessMessage(editingReminder ? 'Reminder updated successfully' : 'Reminder created successfully')
        }
        fetchReminders()
    }

    const handleDeleteReminder = async (reminder) => {
        if (!confirm(`Are you sure you want to delete the reminder "${reminder.name}"?`)) {
            return
        }

        try {
            apiClient.setAuthToken(apiToken)
            await apiClient.deleteGroupReminder(group.id, reminder.id)
            if (onSuccessMessage) {
                onSuccessMessage('Reminder deleted successfully')
            }
            fetchReminders()
        } catch (error) {
            console.error('Failed to delete reminder:', error)
            if (onError) {
                onError('Failed to delete reminder')
            }
        }
    }

    const handleToggleReminder = async (reminder) => {
        try {
            apiClient.setAuthToken(apiToken)
            await apiClient.updateGroupReminder(group.id, reminder.id, {
                ...reminder,
                enabled: !reminder.enabled
            })
            if (onSuccessMessage) {
                onSuccessMessage(`Reminder ${!reminder.enabled ? 'enabled' : 'disabled'} successfully`)
            }
            fetchReminders()
        } catch (error) {
            console.error('Failed to toggle reminder:', error)
            if (onError) {
                onError('Failed to toggle reminder')
            }
        }
    }

    return (
        <div className={styles.groupDetails}>
            <div className={styles.detailsHeader}>
                <h2>{group.name}</h2>
                <button 
                    className={styles.closeButton}
                    onClick={onClose}
                    aria-label="Close group details"
                >
                    ×
                </button>
            </div>
            
            <div className={styles.detailsContent}>
                <div className={styles.detailSection}>
                    <h3>Group Information</h3>
                    <div className={styles.detailGrid}>
                        <div className={styles.detailItem}>
                            <label>Group Leader</label>
                            <span className="color-text-primary">{group.created_by?.name || 'Unknown'}</span>
                        </div>
                        <div className={styles.detailItem}>
                            <label>Leader Email</label>
                            <span className="color-text-primary">{group.created_by?.email || 'Unknown'}</span>
                        </div>
                        <div className={styles.detailItem}>
                            <label>Created Date</label>
                            <span className="color-text-primary">{formatDate(group.created_at)}</span>
                        </div>
                        <div className={styles.detailItem}>
                            <label>Last Updated</label>
                            <span className="color-text-primary">{formatDate(group.updated_at)}</span>
                        </div>
                        <div className={styles.detailItem}>
                            <label>Member Count</label>
                            <span className="color-text-primary">{group.member_count} members</span>
                        </div>
                    </div>
                </div>
                
                <div className={styles.detailSection}>
                    <h3>Description</h3>
                    <p className="color-text-primary">{group.description || 'No description available'}</p>
                </div>
                
                <div className={styles.detailSection}>
                    <div className={styles.sectionHeader}>
                        <h3>Members</h3>
                        <button 
                            className={styles.addMemberButton}
                            onClick={() => setShowAddMemberModal(true)}
                        >
                            + Add Member
                        </button>
                    </div>
                    <div className={styles.membersTableContainer}>
                        {group.members && group.members.length > 0 ? (
                            <table className={styles.membersTable}>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Role</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.members.map((member) => (
                                        <tr key={member.id} className={styles.memberRow}>
                                            <td>
                                                <div className={styles.memberInfo}>
                                                    <span className={styles.memberName}>
                                                        {member.name || member.full_name || 'Unknown'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={styles.memberEmail}>{member.email}</span>
                                            </td>
                                            <td>
                                                <span className={styles.memberRole}>
                                                    {member.role || 'Member'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className={styles.memberActions}>
                                                    <button
                                                        className={styles.removeButton}
                                                        onClick={() => handleRemoveMemberFromGroup(group.id, member.id)}
                                                        aria-label={`Remove ${member.name} from group`}
                                                        title={`Remove ${member.name} from group`}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className={styles.emptyState}>
                                <p>No members yet</p>
                                <button 
                                    className={styles.addFirstMemberButton}
                                    onClick={() => setShowAddMemberModal(true)}
                                >
                                    Add First Member
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.detailSection}>
                    <div className={styles.sectionHeader}>
                        <h3>Reminders</h3>
                        <button
                            className={styles.addMemberButton}
                            onClick={handleAddReminder}
                        >
                            + Add Reminder
                        </button>
                    </div>
                    <div className={styles.remindersTableContainer}>
                        {loadingReminders ? (
                            <div className={styles.loadingState}>
                                <p>Loading reminders...</p>
                            </div>
                        ) : reminders.length > 0 ? (
                            <table className={styles.membersTable}>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Capacity</th>
                                        <th>Schedule</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reminders.map((reminder) => (
                                        <tr key={reminder.id} className={styles.memberRow}>
                                            <td>
                                                <div className={styles.memberInfo}>
                                                    <span className={styles.memberName}>
                                                        {reminder.name}
                                                    </span>
                                                    {reminder.description && (
                                                        <span className={styles.reminderDescription}>
                                                            {reminder.description}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={styles.memberEmail}>
                                                    {reminder.work_capacity_hours} hrs/week
                                                </span>
                                            </td>
                                            <td>
                                                <span className={styles.memberRole}>
                                                    {reminder.send_day.charAt(0).toUpperCase() + reminder.send_day.slice(1)} at {reminder.formatted_send_time}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={reminder.enabled ? styles.statusEnabled : styles.statusDisabled}>
                                                    {reminder.enabled ? 'Enabled' : 'Disabled'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className={styles.memberActions}>
                                                    <button
                                                        className={styles.editButton}
                                                        onClick={() => handleEditReminder(reminder)}
                                                        aria-label={`Edit ${reminder.name}`}
                                                        title={`Edit ${reminder.name}`}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className={styles.toggleButton}
                                                        onClick={() => handleToggleReminder(reminder)}
                                                        aria-label={`${reminder.enabled ? 'Disable' : 'Enable'} ${reminder.name}`}
                                                        title={`${reminder.enabled ? 'Disable' : 'Enable'} ${reminder.name}`}
                                                    >
                                                        {reminder.enabled ? 'Disable' : 'Enable'}
                                                    </button>
                                                    <button
                                                        className={styles.removeButton}
                                                        onClick={() => handleDeleteReminder(reminder)}
                                                        aria-label={`Delete ${reminder.name}`}
                                                        title={`Delete ${reminder.name}`}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className={styles.emptyState}>
                                <p>No reminders configured yet</p>
                                <button
                                    className={styles.addFirstMemberButton}
                                    onClick={handleAddReminder}
                                >
                                    Add First Reminder
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.detailSection}>
                    <h3>Actions</h3>
                    <div className={styles.detailActions}>
                        <button 
                            className={styles.primaryButton}
                            onClick={() => setShowEditGroupModal(true)}
                        >
                            Edit Group
                        </button>
                        <button
                            className={styles.dangerButton}
                            onClick={() => onArchive && onArchive(group)}
                        >
                            Archive Group
                        </button>
                    </div>
                </div>
            </div>

            {/* Add User to Group Modal */}
            <AddUserToGroupModal
                isOpen={showAddMemberModal}
                onClose={() => setShowAddMemberModal(false)}
                onSave={handleMemberAddedToGroup}
                group={group}
                apiToken={apiToken}
            />

            {/* Edit Group Modal */}
            <EditGroupModal
                isOpen={showEditGroupModal}
                onClose={() => setShowEditGroupModal(false)}
                onSave={handleGroupUpdated}
                group={group}
                apiToken={apiToken}
            />

            {/* Group Reminder Modal */}
            <GroupReminderModal
                isOpen={showReminderModal}
                onClose={() => {
                    setShowReminderModal(false)
                    setEditingReminder(null)
                }}
                onSave={handleReminderSaved}
                group={group}
                reminder={editingReminder}
                apiToken={apiToken}
            />
        </div>
    )
}